import { CalendarCheck, Sun } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import { formatDateShort, scoreColor } from "../lib/format";
import type { BestDeparture, UiLang } from "../lib/types";

interface Props {
  bestDeparture: BestDeparture;
  currentDate: string;
  lang: UiLang;
  onPickDate: (date: string) => void;
}

/**
 * Anbefaling av avreisedag.
 *
 * Hele turen scores for hver mulige startdato i MET sitt varselvindu, og
 * hver dag vises som en søyle. Poenget er at brukeren skal kunne overprøve
 * anbefalingen: tallene ligger der, ikke bare konklusjonen.
 */
export function BestDayPanel({ bestDeparture, currentDate, lang, onPickDate }: Props) {
  const t = dictionaries[lang];
  const { best, candidates, headline, inconclusive } = bestDeparture;

  return (
    <div className="card best-day">
      <div className="card-title">
        <CalendarCheck size={14} style={{ verticalAlign: -2, marginRight: 7 }} />
        {t.bestDayTitle}
      </div>
      <p className="best-day-headline">{headline}</p>

      <div className="best-day-bars" role="list" aria-label={t.bestDayCompare}>
        {candidates.map((candidate) => {
          const isBest = candidate.date === best.date;
          const isCurrent = candidate.date === currentDate;
          return (
            <button
              key={candidate.date}
              role="listitem"
              className={`best-day-bar ${isCurrent ? "best-day-bar--current" : ""}`}
              onClick={() => onPickDate(candidate.date)}
              title={`${candidate.label} · ${t.daylightHours(candidate.daylightHours)}`}
            >
              <span className="best-day-score" style={{ color: scoreColor(candidate.score) }}>
                {candidate.score}
              </span>
              <span className="best-day-column">
                <span
                  className="best-day-fill"
                  style={{
                    height: `${Math.max(6, candidate.score)}%`,
                    background: scoreColor(candidate.score),
                    opacity: isBest ? 1 : 0.55,
                  }}
                />
              </span>
              <span className={`best-day-date ${isBest ? "best-day-date--best" : ""}`}>
                {formatDateShort(candidate.date, lang)}
              </span>
            </button>
          );
        })}
      </div>

      {!inconclusive && best.date !== currentDate && (
        <button className="best-day-apply" onClick={() => onPickDate(best.date)}>
          <Sun size={13} />
          {t.bestDayPickThis} — {formatDateShort(best.date, lang)}
        </button>
      )}
    </div>
  );
}
